import {getDsContext} from "@/utils/dsContext";
import {preparePlayerCareer, careerSourceLabel} from "@/utils/playerCareer";
import {useScoutingVisions} from "@/utils/scoutingVisionData";
import {useScoutingReports} from "@/utils/scoutingReportData";
import ScoutingReportsStatus from "@/components/ScoutingReportsStatus";
import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import CardsTable from "@/components/CardsTable";
import ViewField from "@/components/Input/ViewField";
import ValuationsPage from "@/pages/Valuations";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import VLFModal from "@/components/Modal";
import HandleCareerModal from "@/pages/Scouting/Players/Player/handleCareer";
import Tabs from "@/components/Tabs";
import ClubCrest from "@/components/ClubCrest";
import PlayerHero from "@/components/PlayerHero";
import {printColor} from "@/style/utils";
import PositionsPitch from "@/components/PositionsPitch";

import {AttachmentAPI} from "@/api/attachment";
import {PlayerAPI} from "@/api/player";
import {ScoutingReportAPI} from "@/api/scouting_report";
import {PlayerValuationAPI} from "@/api/player_valuation";

import EnrollPlayerModal from "@/components/EnrollPlayerModal";
import PlayerSeasonStats from "@/components/PlayerSeasonStats";
import PlayerMatchStats from "@/components/PlayerMatchStats";
import WyscoutImport from "@/components/WyscoutImport";
import PlayerVisions from "@/components/PlayerVisions";
import SharedPlayerProfile from "@/components/SharedPlayerProfile";

import ArrowBack from "@mui/icons-material/ArrowBack";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PrintIcon from "@mui/icons-material/Print";
import RateReviewIcon from "@mui/icons-material/RateReview";
import BadgeIcon from "@mui/icons-material/Badge";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import {
	dateFormat,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getAttachment} from "@/utils/functions";
import {statureFromHeight, STATURE_LABELS} from "@/utils/scoutingData";
import {useProviderMatches} from "@/utils/scoutingStatisticsData";
import Alert from "@mui/material/Alert";
import {playerVisions} from "@/utils/scoutingVisions";
import {buildProfileInputs} from "@/utils/aiProfile";
import {
	formatAverage,
	getVerdict,
	playerReportsAverage,
} from "@/utils/scoutingReports";
import {
	useScoutingPipeline,
	getPipelineStage,
	toggleMonitored,
} from "@/utils/scoutingPipeline";

import "./index.scss";
import {useTranslation} from "react-i18next";

const ScoutingPlayerPage = (props) => {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [player, setPlayer] = useState({});

	const [idAttachment, setIdAttachment] = useState();
	const [attachments, setAttachments] = useState([]);

	// Pipeline & rating
	const {
		pipeline,
		canWrite: canManagePipeline,
		error: pipelineError,
	} = useScoutingPipeline();
	const pipelineEntry = pipeline[id] || null;
	const [valuations, setValuations] = useState([]);
	const [scoutValuations, setScoutValuations] = useState([]);
	const ratingValuations = useMemo(
		() => [...valuations, ...scoutValuations],
		[valuations, scoutValuations],
	);

	const [initialData, setInitialData] = useState();

	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openCareerDetail, setOpenCareerDetail] = useState(false);
	const [openHandleAttachmentConfirm, setOpenHandleAttachmentConfirm] =
		useState(false);
	const [openEnrollModal, setOpenEnrollModal] = useState(false);
	const [currentTab, setCurrentTab] = useState("profile");
	const providerState = useProviderMatches(id);
	const matchStats =
		providerState.status === "ready" && providerState.data?.exists
			? providerState.data
			: null;
	const visionState = useScoutingVisions();
	const visions = useMemo(
		() => playerVisions(id, visionState.visions),
		[id, visionState.visions],
	);
	const reportData = useScoutingReports();
	const relazioni = useMemo(
		() => reportData.reports.filter((r) => r.playerId === id),
		[id, reportData.reports],
	);
	const user = useSelector((state) => state?.user);
	const profileInputs = buildProfileInputs(player, {
		visions,
		matches: matchStats?.matches,
	});
	const lastVerdict = getVerdict(relazioni[0]?.verdict);
	const newRelazione = () => navigate("/scouting/relazioni/new?playerId=" + id);
	const renderRelazione = (r) => {
		const v = getVerdict(r.verdict);
		return (
			<div
				key={r.id}
				className="intel-report-row"
				onClick={() => navigate("/scouting/relazioni/" + r.id)}
			>
				<span className="intel-report-row__date">
					{r.reportDate ? moment(r.reportDate).format("DD/MM/YY") : "—"}
				</span>
				<span className="intel-report-row__scout">
					{[r.author?.name, r.template?.name].filter(Boolean).join(" · ") ||
						"—"}
				</span>
				<span className="intel-report-row__verdict" style={{color: v?.color}}>
					{v?.label}
				</span>
				<span className="intel-report-row__average">
					{formatAverage(r.average)}
				</span>
			</div>
		);
	};
	const openMatch = (matchId) =>
		navigate("/matches/" + matchId, {state: {initialTab: "scouting"}});

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id, {seasonId});
			if (response?.isInternal === true) {
				navigate("/players/" + id, {replace: true});
				return;
			}
			response.career = preparePlayerCareer(response.career || []);
			setPlayer(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	const getPlayerAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("player", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const deletePlayer = async () => {
		setLoading(true);
		try {
			await PlayerAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/scouting/players");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};
	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(idAttachment);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleAttachmentConfirm(false);
			getPlayerAttachment();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getPlayer();
		getPlayerAttachment();
	}, [id, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		let active = true;
		setValuations([]);
		setScoutValuations([]);
		PlayerValuationAPI.playerList(id, {seasonId})
			.then((vals) => {
				const v = vals || [];
				if (active) setValuations(v);
			})
			.catch(() => {
				if (active) setValuations([]);
			});
		ScoutingReportAPI.getByPlayerId(id, {seasonId})
			.then((rows) => {
				if (active) setScoutValuations(rows || []);
			})
			.catch(() => {
				if (active) setScoutValuations([]);
			});
		return () => {
			active = false;
		};
	}, [id, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleCareerDetail = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				...data,
				playerId: player?.id,
				seasonId: data?.careerSeason?.value,
				clubId: data?.careerClub?.value,
				playerCategoryId: data?.careerCategory?.value,
			};

			id
				? await PlayerAPI.updateCareer(player?.id, id, body)
				: await PlayerAPI.createCareer(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			setOpenCareerDetail(false);
			setInitialData();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	const deleteCareerDetail = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteCareer(player?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const goBack = () => {
		navigate("/scouting/players");
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <RateReviewIcon />,
			title: "Nuova relazione",
			disabled: !reportData.canWrite || !player?.id,
			onClick: () => navigate("/scouting/relazioni/new?playerId=" + player.id),
		},
		...(!player?.isInternal
			? [
					{
						icon: <BadgeIcon />,
						title: "Tessera",
						onClick: () => setOpenEnrollModal(true),
						color: "success",
					},
				]
			: []),
		{
			icon: <EditIcon />,
			title: "Modifica",
			variant: "outlined",
			onClick: () => navigate("/scouting/players/" + player?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	// ── Computed for Intel Card ───────────────────────────────────────────────

	const currentClub =
		(player?.memberships || [])
			.map((r) => r.club?.name || r.name)
			.filter(Boolean)
			.join(" / ") ||
		player?.roster?.club?.name ||
		(player?.career?.length
			? [...player.career].sort((a, b) =>
					(b.season?.name || "").localeCompare(a.season?.name || ""),
				)[0]?.club?.name
			: null);

	const role =
		player?.internalRole?.code ||
		(player?.goalkeeper
			? "Portiere"
			: player?.secondaryRole || player?.primaryRole);
	const height = player?.height || player?.lastHeight;
	const stature = statureFromHeight(height);
	const heightLabel = height
		? height + " cm" + (stature ? " · " + STATURE_LABELS[stature] : "")
		: "";
	const competitions = (player?.roster?.competitionSeasons || [])
		.map((c) => c.competition?.name)
		.filter(Boolean);
	const pipelineStage = pipelineEntry
		? getPipelineStage(pipelineEntry.status)
		: null;

	const handleToggleMonitored = async () => {
		try {
			const entry = await toggleMonitored(id);
			if (entry && entry.status !== "watching")
				handleNotification(
					"Già in pipeline: " + getPipelineStage(entry.status)?.label,
					"info",
				);
		} catch (error) {
			handleNotification(
				error.response?.data?.error ||
					error.message ||
					"Monitoraggio non salvato",
				"error",
			);
		}
	};

	const mainConfiguration = [
		{
			value: (player?.alternativeRoles || [])
				.map((r) => `${r.code || ""} · ${r.name}`)
				.join(" / "),
			label: "Ruoli alternativi",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.internalRole
				? `${player.internalRole.code || ""} · ${player.internalRole.name}`
				: null,
			label: "Ruolo interno",
			size: "col-6 col-lg-4",
		},
		{value: player?.somatotype, label: "Somatotipo", size: "col-6 col-lg-4"},
		{value: player?.proportions, label: "Proporzioni", size: "col-6 col-lg-4"},
		{
			value: (player?.attitudes || []).join(" · "),
			label: "Attitudini",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.firstName,
			label: t("name"),
			size: "col-6 col-lg-4",
		},
		{
			value: player?.lastName,
			label: "Cognome",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.dateOfBirth
				? moment(player?.dateOfBirth).format(dateFormat)
				: null,
			label: "Data di nascita",
			size: "col-6 col-lg-4",
		},
		{
			value: heightLabel,
			label: "Altezza",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.weight ? player.weight + " kg" : "",
			label: "Peso",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.goalkeeper ? "Portiere" : player?.primaryRole,
			label: "Ruolo",
			size: "col-6 col-lg-4",
		},
		...(!player?.goalkeeper && player?.secondaryRole
			? [
					{
						value: player.secondaryRole,
						label: "Ruolo specifico",
						size: "col-6 col-lg-4",
					},
				]
			: []),
		...(player?.goalkeeper
			? [
					{
						value: player?.favoriteHand,
						label: "Mano",
						size: "col-6 col-lg-4",
					},
				]
			: []),
		{
			value: player?.favoriteFoot,
			label: "Piede",
			size: "col-6 col-lg-4",
		},
		{
			value: player?.nationality,
			label: "Nazionalità",
			size: "col-6 col-lg-4",
		},
		...(currentClub
			? [
					{
						value: currentClub,
						label: "Squadra attuale",
						size: "col-6 col-lg-4",
					},
				]
			: []),
	];

	const careerColumns = [
		{
			label: "Stagione",
			key: "season",
			customRender: (item) => item.season?.name,
		},
		{
			label: "Squadra",
			key: "club",
			customRender: (item) => (
				<span className="d-inline-flex align-items-center gap-2">
					<ClubCrest
						name={item.club?.name}
						logoUrl={item.club?.imageUrl}
						colors={item.club?.colors}
					/>
					{item.teamName || item.club?.name}
				</span>
			),
		},
		{
			label: "Categoria",
			key: "playerCategory",
			customRender: (item) => item.playerCategory?.name,
		},
		{
			label: "Competizione",
			key: "competition",
			customRender: (item) => item.competition?.name || "—",
		},
		{label: "Fonte", key: "source", customRender: careerSourceLabel},
	];

	const breadcrumbsElements = [
		{label: "Giocatori osservati", path: "/scouting/players"},
		{label: "/"},
	];

	// ── Print scheda ──────────────────────────────────────────────────────────

	const handlePrintScheda = () => {
		if (reportData.status !== "ready" || visionState.status !== "ready") {
			handleNotification(
				"Attendi il caricamento dei dati scouting prima di stampare",
				"error",
			);
			return;
		}
		const esc = (s) =>
			String(s || "")
				.replace(/&/g, "&amp;")
				.replace(/</g, "&lt;")
				.replace(/>/g, "&gt;");

		const fullName = esc(
			((player.firstName || "") + " " + (player.lastName || "")).trim(),
		);
		const role = esc(player.goalkeeper ? "Portiere" : player.primaryRole || "");
		const secRole = player.internalRole
			? esc(`${player.internalRole.code || ""} · ${player.internalRole.name}`)
			: !player.goalkeeper && player.secondaryRole
				? esc(player.secondaryRole)
				: null;
		const age = player.dateOfBirth
			? moment().diff(player.dateOfBirth, "years")
			: null;
		const birthDate = player.dateOfBirth
			? moment(player.dateOfBirth).format(dateFormat)
			: null;
		const foot = player.favoriteFoot ? esc(player.favoriteFoot) : null;
		const physical = [
			player.height && `${player.height} cm`,
			player.weight && `${player.weight} kg`,
			player.somatotype && `Somatotipo ${esc(player.somatotype)}`,
			player.proportions && `Proporzioni ${esc(player.proportions)}`,
			(player.attitudes || []).length > 0 &&
				`Attitudini ${esc(player.attitudes.join(" / "))}`,
			(player.alternativeRoles || []).length > 0 &&
				`Alternativi ${esc(player.alternativeRoles.map((r) => r.code || r.name).join(" / "))}`,
		]
			.filter(Boolean)
			.join(" · ");
		const clubStr = currentClub ? esc(currentClub) : null;
		const pipelineStageInfo = pipelineStage;
		const pipelineHTML = pipelineStageInfo
			? `<span class="pbadge" style="color:${printColor(pipelineStageInfo.color)};background:${printColor(pipelineStageInfo.bg)};border:1.5px solid ${printColor(pipelineStageInfo.border)}">${esc(pipelineStageInfo.label)}</span>`
			: "";

		const avgStr = formatAverage(playerReportsAverage(relazioni));
		const lastVerdictStr = lastVerdict
			? `<span style="color:${printColor(lastVerdict.color)}">${esc(lastVerdict.label)}</span>`
			: "—";
		const reportsRows = relazioni.length
			? relazioni
					.map(
						(r) =>
							`<tr><td>${esc(r.reportDate ? moment(r.reportDate).format(dateFormat) : "—")}</td><td>${esc(r.author?.name || "—")}</td><td>${esc(getVerdict(r.verdict)?.label || "—")}</td><td>${esc(formatAverage(r.average))}</td></tr>`,
					)
					.join("")
			: '<tr><td colspan="4" class="empty">Nessuna relazione</td></tr>';

		// Career rows
		const careerRows = player?.career?.length
			? [...player.career]
					.sort((a, b) =>
						(b.season?.name || "").localeCompare(a.season?.name || ""),
					)
					.map(
						(c) =>
							`<tr><td>${esc(c.season?.name || "—")}</td><td>${esc(c.teamName || c.club?.name || "—")}</td><td>${esc(c.playerCategory?.name || "—")}</td><td>${esc(c.competition?.name || "—")}</td><td>${esc(careerSourceLabel(c))}</td></tr>`,
					)
					.join("")
			: '<tr><td colspan="5" class="empty">Nessuna carriera</td></tr>';

		const notesHtml = player?.notes
			? `<div class="section"><div class="stitle">Note</div><p class="notes">${esc(player.notes)}</p></div>`
			: "";

		const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Scheda ${fullName}</title>
<style>
@page{size:A4 portrait;margin:15mm}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:system-ui,sans-serif;color:#0b1f44;print-color-adjust:exact;-webkit-print-color-adjust:exact}
.page{display:flex;flex-direction:column;gap:10mm;min-height:calc(297mm - 30mm)}
.hdr{border-bottom:2px solid #0b1f44;padding-bottom:5mm}
.hdr-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:3mm}
.fname{font-size:22px;font-weight:800;letter-spacing:-.02em}
.pbadge{font-size:10px;font-weight:700;border-radius:999px;padding:2px 10px}
.tags{display:flex;flex-wrap:wrap;gap:4px;margin-top:3mm}
.tag{font-size:10px;font-weight:600;color:#475569;background:#f1f5f9;border-radius:999px;padding:2px 10px}
.role-tag{background:#0b1f44;color:#fff}
.kpi-row{display:flex;gap:5mm}
.kpi{flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:4mm 5mm;print-color-adjust:exact;-webkit-print-color-adjust:exact}
.klabel{font-size:8px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em;margin-bottom:2mm}
.kvalue{font-size:18px;font-weight:800;color:#0b1f44}
.ksub{font-size:9px;color:#94a3b8;margin-top:1mm}
.section{display:flex;flex-direction:column;gap:3mm;break-inside:avoid;page-break-inside:avoid}
.stitle{font-size:9px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:.06em;border-bottom:1px solid #f1f5f9;padding-bottom:2mm;margin-bottom:1mm}
table{width:100%;border-collapse:collapse}
th{font-size:8px;font-weight:700;color:#94a3b8;text-transform:uppercase;padding:2mm 2mm;text-align:left;border-bottom:1.5px solid #f1f5f9}
td{font-size:10px;color:#334155;padding:2.5mm 2mm;border-bottom:1px solid #f8fafc}
tr:last-child td{border-bottom:none}
.empty{color:#94a3b8;font-style:italic}
.trend-row{display:flex;align-items:flex-start;gap:3mm;flex-wrap:wrap;padding:2mm 0}
.vscore{display:inline-flex;flex-direction:column;align-items:center;background:#f1f5f9;border:1px solid #e2e8f0;border-radius:5px;padding:2mm 3mm;min-width:11mm;text-align:center;font-size:12px;font-weight:800;print-color-adjust:exact;-webkit-print-color-adjust:exact}
.vscore small{font-size:7px;color:#94a3b8;font-weight:500;margin-top:1mm;display:block}
.vscore--cur{background:#0b1f44;color:#fff;border-color:#0b1f44}
.vscore--cur small{color:rgba(255,255,255,.7)}
.varrow{font-size:10px;color:#cbd5e1;margin-top:5mm;display:inline-block}
.notes{font-size:11px;color:#475569;line-height:1.6}
.footer{font-size:8px;color:#94a3b8;text-align:right;margin-top:auto;padding-top:4mm;border-top:1px solid #f1f5f9}
</style></head><body>
<div class="page">
  <div class="hdr">
    <div class="hdr-top">
      <div class="fname">${fullName}</div>
      <div>${pipelineHTML}</div>
    </div>
    <div class="tags">
      ${role ? `<span class="tag role-tag">${role}</span>` : ""}
      ${secRole ? `<span class="tag">${secRole}</span>` : ""}
      ${age ? `<span class="tag">${age} anni · ${birthDate}</span>` : ""}
      ${foot ? `<span class="tag">Piede ${foot}</span>` : ""}
      ${physical ? `<span class="tag">${physical}</span>` : ""}
      ${clubStr ? `<span class="tag">${clubStr}</span>` : ""}
    </div>
  </div>
  <div class="kpi-row">
    <div class="kpi"><div class="klabel">Media relazioni</div><div class="kvalue">${avgStr}</div><div class="ksub">${relazioni.length ? `${relazioni.length} relazion${relazioni.length === 1 ? "e" : "i"}` : "Nessuna"}</div></div>
    <div class="kpi"><div class="klabel">Ultimo verdetto</div><div class="kvalue" style="font-size:13px">${lastVerdictStr}</div></div>
    <div class="kpi"><div class="klabel">Visioni</div><div class="kvalue">${visionState.status === "ready" ? visions.length : "—"}</div></div>
  </div>
  <div class="section"><div class="stitle">Relazioni</div><table><thead><tr><th>Data</th><th>Osservatore</th><th>Verdetto</th><th>Media</th></tr></thead><tbody>${reportsRows}</tbody></table></div>
  <div class="section"><div class="stitle">Carriera</div><table><thead><tr><th>Stagione</th><th>Squadra</th><th>Categoria</th><th>Competizione</th><th>Fonte</th></tr></thead><tbody>${careerRows}</tbody></table></div>
  ${notesHtml}
  <div class="footer">Scheda Intelligence · ${fullName} · Generata il ${new Date().toLocaleDateString("it-IT", {day: "2-digit", month: "long", year: "numeric"})}</div>
</div>
<script>window.onload=()=>{window.print();}</script>
</body></html>`;

		const w = window.open("", "_blank", "width=900,height=1200");
		if (w) {
			w.document.write(html);
			w.document.close();
		}
	};

	return (
		<Page className="page-container">
			<ScoutingReportsStatus data={reportData} />
			{visionState.readError && (
				<div role="alert">
					Visioni non disponibili.{" "}
					<MainActions
						actions={[
							{
								label: "Riprova",
								onClick: () => visionState.refresh().catch(() => {}),
							},
						]}
					/>
				</div>
			)}
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					player ? (player.firstName || "") + " " + (player.lastName || "") : ""
				}
				actions={
					<MainActions
						actions={[
							...mainActions.slice(0, -2),
							{
								icon: <PrintIcon />,
								title: "Stampa scheda",
								variant: "outlined",
								onClick: handlePrintScheda,
							},
							...mainActions.slice(-2),
						]}
					/>
				}
			/>
			<div className="sheet-container scouting-player-container">
				<PlayerHero
					player={player}
					chips={[
						role && {id: "role", label: role, tone: "primary"},
						currentClub && {
							id: "club",
							label: (
								<>
									<ClubCrest
										name={currentClub}
										logoUrl={player.roster?.club?.imageUrl}
										colors={player.roster?.club?.colors}
										size={14}
									/>
									{currentClub}
								</>
							),
						},
						...competitions.map((c) => ({id: "competition-" + c, label: c})),
						player.dateOfBirth && {
							id: "age",
							label:
								moment().diff(player.dateOfBirth, "years") +
								" anni · " +
								moment(player.dateOfBirth).format("YYYY"),
						},
						player.favoriteFoot && {
							id: "foot",
							label: "Piede " + player.favoriteFoot,
						},
						player.nationality && {
							id: "nationality",
							label: player.nationality,
						},
						heightLabel && {id: "height", label: heightLabel},
					].filter(Boolean)}
					scores={[
						{
							id: "reports",
							label: "Relazioni",
							value: playerReportsAverage(relazioni),
							max: 5,
						},
					]}
					aside={
						<div className="intel-hero-aside">
							<MainActions
								actions={[
									{
										icon: pipelineEntry ? <StarIcon /> : <StarBorderIcon />,
										label: pipelineEntry ? "Monitorato" : "Da monitorare",
										title: pipelineEntry
											? "Togli da monitorare"
											: "Aggiungi a da monitorare",
										variant: pipelineEntry ? undefined : "outlined",
										onClick: handleToggleMonitored,
										disabled: !canManagePipeline || !!pipelineError,
									},
								]}
							/>
							{pipelineStage && pipelineStage.id !== "watching" && (
								<div
									className="sp-pipeline-badge"
									style={{
										background: pipelineStage.bg,
										borderColor: pipelineStage.border,
										marginBottom: 0,
									}}
									onClick={() => navigate("/scouting/pipeline")}
								>
									<span
										className="sp-pipeline-badge__dot"
										style={{background: pipelineStage.dot}}
									/>
									<span
										className="sp-pipeline-badge__label"
										style={{color: pipelineStage.color}}
									>
										{pipelineStage.label}
									</span>
									<span className="sp-pipeline-badge__sub">
										{moment(pipelineEntry.addedAt).fromNow()}
									</span>
									<ArrowForwardIcon
										sx={{
											fontSize: 13,
											color: pipelineStage.color,
											opacity: 0.7,
										}}
									/>
								</div>
							)}
						</div>
					}
				/>
				<Tabs
					freeHeight
					tabs={[
						{
							key: "profile",
							label: "Profilo",
							render: () => (
								<>
									<div className="intel-card">
										<div className="intel-profile-grid">
											<div className="intel-section">
												<div className="intel-section__title">Posizioni</div>
												<PositionsPitch
													primary={role}
													alternatives={(player?.alternativeRoles || [])
														.map((r) => r.code || r.name)
														.filter(Boolean)}
												/>
											</div>
											<div className="intel-section">
												<div className="intel-section__title">
													Dati anagrafici e fisici
												</div>
												<ViewField configuration={mainConfiguration} />
											</div>
										</div>
										{/* KPI row */}
										<div className="intel-kpi-row">
											<div className="intel-kpi">
												<div className="intel-kpi__label">Media relazioni</div>
												<div className="intel-kpi__value">
													{formatAverage(playerReportsAverage(relazioni))}
												</div>
												<div className="intel-kpi__sub">
													{relazioni.length
														? `${relazioni.length} relazion${relazioni.length === 1 ? "e" : "i"}`
														: reportData.status === "ready"
															? "Nessuna relazione"
															: "Relazioni non disponibili"}
												</div>
											</div>
											<div className="intel-kpi">
												<div className="intel-kpi__label">Ultimo verdetto</div>
												<div
													className="intel-kpi__value"
													style={
														lastVerdict ? {color: lastVerdict.color} : undefined
													}
												>
													{lastVerdict?.label || "—"}
												</div>
												<div className="intel-kpi__sub">
													{relazioni[0]?.reportDate
														? moment(relazioni[0].reportDate).format(dateFormat)
														: reportData.status === "ready"
															? "Nessuna relazione"
															: "Relazioni non disponibili"}
												</div>
											</div>
											<div className="intel-kpi">
												<div className="intel-kpi__label">Visioni</div>
												<div className="intel-kpi__value">
													{visionState.status === "ready"
														? visions.length
														: "—"}
												</div>
												<div className="intel-kpi__sub">
													{visions[0]?.matchDate
														? "Ultima " +
															moment(visions[0].matchDate).format(dateFormat)
														: "Mai visto"}
												</div>
											</div>
										</div>

										<SharedPlayerProfile
											playerId={id}
											player={player}
											inputs={profileInputs}
										/>

										{visions.length > 0 && (
											<div className="intel-section">
												<div className="intel-section__title">
													Ultime visioni
												</div>
												<PlayerVisions
													visions={visions}
													limit={3}
													onOpenMatch={openMatch}
													exportMatches={matchStats?.matches}
													observerId={user?.userId}
													onCreateReport={
														reportData.canWrite
															? (v) =>
																	navigate(
																		"/scouting/relazioni/new?playerId=" + id,
																		{
																			state: {
																				visionId: v.visionId,
																				match: {
																					id: v.matchId,
																					name: [v.homeTeamName, v.awayTeamName]
																						.filter(Boolean)
																						.join(" – "),
																				},
																			},
																		},
																	)
															: undefined
													}
												/>
											</div>
										)}

										{relazioni[0] && (
											<div className="intel-section">
												<div className="intel-section__title">
													Ultima relazione
												</div>
												{renderRelazione(relazioni[0])}
												<p className="intel-judgment">
													{relazioni[0].judgment}
												</p>
											</div>
										)}

										{/* Notes if present */}
										{player?.notes && (
											<div className="intel-notes">
												<span className="intel-notes__label">Note</span>
												<span className="intel-notes__text">
													{player.notes}
												</span>
											</div>
										)}
									</div>
									<div className="row no-margin mt-3">
										<div className="col-sm-12 padding-small">
											<CardsTable
												title="Carriera · Club"
												fullHeight={false}
												middleHeight
												configurationColumns={careerColumns}
												data={player?.career?.filter(
													(row) => row.club?.isNational !== true,
												)}
												actions={[
													{
														icon: <ControlPointIcon />,
														onClick: () => setOpenCareerDetail(true),
													},
												]}
												handleEdit={(item) => {
													setInitialData(Object.assign({}, item));
													setOpenCareerDetail(true);
												}}
												handleDelete={(item) => {
													deleteCareerDetail(item.id);
												}}
												defaultSortedField="dateEvent"
												defaultSortedOrder="descend"
											/>
											{player?.career?.some(
												(row) => row.club?.isNational === true,
											) && (
												<>
													<CardsTable
														title="Carriera · Nazionali"
														fullHeight={false}
														middleHeight
														configurationColumns={careerColumns}
														data={player?.career?.filter(
															(row) => row.club?.isNational === true,
														)}
														handleEdit={(item) => {
															setInitialData(Object.assign({}, item));
															setOpenCareerDetail(true);
														}}
														handleDelete={(item) => {
															deleteCareerDetail(item.id);
														}}
														defaultSortedField="dateEvent"
														defaultSortedOrder="descend"
													/>
												</>
											)}
										</div>
									</div>
									<div className="row no-margin mt-3">
										<div className="col-sm-12 padding-small">
											<VLFCard
												title="Allegati"
												content={
													<AttachmentsContainer
														entityId={id}
														entityType="player"
														attachments={attachments}
														handleAdd={() => getPlayerAttachment()}
														handleDownload={handleDownload}
														handleDelete={(id) => {
															setIdAttachment(id);
															setOpenHandleAttachmentConfirm(true);
														}}
														handleNotification={handleNotification}
													/>
												}
											/>
											<VLFModal
												open={openHandleAttachmentConfirm}
												title="Eliminazione"
												content={
													<div className="mt-2">
														Sei sicuro di voler eliminare questo elemento?
													</div>
												}
												confirm={() => deleteAttachment()}
												close={() => setOpenHandleAttachmentConfirm(false)}
												confirmLabel="Conferma"
											/>
										</div>
									</div>
								</>
							),
						},
						{
							key: "views",
							label: "Visioni",
							render: () =>
								visions.length ? (
									<div className="intel-card">
										<PlayerVisions
											visions={visions}
											onOpenMatch={openMatch}
											exportMatches={matchStats?.matches}
											observerId={user?.userId}
											onCreateReport={
												reportData.canWrite
													? (v) =>
															navigate(
																"/scouting/relazioni/new?playerId=" + id,
																{
																	state: {
																		visionId: v.visionId,
																		match: {
																			id: v.matchId,
																			name: [v.homeTeamName, v.awayTeamName]
																				.filter(Boolean)
																				.join(" – "),
																		},
																	},
																},
															)
													: undefined
											}
										/>
									</div>
								) : (
									<div className="intel-card">
										<div className="intel-empty">
											<div className="intel-empty__title">
												{visionState.status === "ready"
													? "Mai visto"
													: visionState.loading
														? "Caricamento visioni…"
														: "Visioni non disponibili"}
											</div>
											<div className="intel-empty__text">
												{visionState.status === "ready"
													? "Nessuna visione di gara registrata per questo giocatore."
													: "Attendi il caricamento o riprova."}
											</div>
											<MainActions
												actions={[
													{
														label: "Trova la prossima gara",
														title: "Trova la prossima gara",
														onClick: () => navigate("/scouting/planning"),
													},
												]}
											/>
										</div>
									</div>
								),
						},
						{
							key: "reports",
							label: "Relazioni",
							render: () => (
								<>
									<div className="intel-card">
										<div className="intel-section">
											<div className="intel-section__title intel-section__title--action">
												Relazioni
												<MainActions
													actions={[
														{
															icon: <RateReviewIcon />,
															label: "Nuova relazione",
															title: "Nuova relazione",
															disabled: !reportData.canWrite,
															onClick: newRelazione,
														},
													]}
												/>
											</div>
											{relazioni.length ? (
												relazioni.map(renderRelazione)
											) : (
												<div className="intel-report-empty">
													{reportData.status === "ready"
														? "Nessuna relazione su questo giocatore."
														: "Relazioni non disponibili."}
												</div>
											)}
										</div>
									</div>
									{ratingValuations.length > 0 && (
										<div className="row no-margin mt-3">
											<div className="col-sm-12 padding-small">
												<div className="table-title px-1">
													Storico valutazioni
												</div>
												<ValuationsPage
													external
													expanded={false}
													playerId={id}
													allowedTypes={["scout", "quick"]}
													handleNotification={handleNotification}
												/>
											</div>
										</div>
									)}
								</>
							),
						},
						{
							key: "stats",
							label: "Statistiche",
							render: () => (
								<div className="row no-margin mt-3">
									<div className="col-sm-12 padding-small">
										<div className="intel-stats-head">
											{matchStats && (
												<span>
													{matchStats.fileName} · importato il{" "}
													{moment(matchStats.importedAt).format("DD/MM/YYYY")}
												</span>
											)}
											<WyscoutImport
												key={id + ":" + getDsContext()}
												player={player}
												handleNotification={handleNotification}
												canWrite={providerState.canWrite}
											/>
										</div>
										{providerState.status === "ready" &&
											!providerState.data?.exists && (
												<p>
													Nessun export Wyscout condiviso. Le statistiche
													registrate sono mostrate separatamente.
												</p>
											)}
										{providerState.status === "error" ? (
											<Alert severity="error">
												Statistiche Wyscout non disponibili.{" "}
												<button
													onClick={() =>
														providerState.refresh().catch(() => {})
													}
												>
													Riprova statistiche
												</button>
											</Alert>
										) : providerState.status === "idle" ||
										  providerState.status === "loading" ? (
											<p>Caricamento statistiche Wyscout…</p>
										) : matchStats ? (
											<PlayerMatchStats
												key={matchStats.importedAt}
												data={matchStats}
												goalkeeper={!!player?.goalkeeper}
												attitudes={player?.attitudes}
											/>
										) : (
											<PlayerSeasonStats playerData={player} />
										)}
									</div>
								</div>
							),
						},
					]}
					currentTab={currentTab}
					setCurrentTab={setCurrentTab}
				/>
				<VLFModal
					open={openHandleConfirm}
					title="Eliminazione"
					content={
						<div className="mt-2">
							Sei sicuro di voler eliminare questo elemento?
						</div>
					}
					confirm={() => {
						deletePlayer();
					}}
					close={() => setOpenHandleConfirm(false)}
					confirmLabel="Conferma"
				/>
				{openCareerDetail ? (
					<HandleCareerModal
						open={openCareerDetail}
						confirm={handleCareerDetail}
						close={() => {
							setOpenCareerDetail(false);
							setInitialData();
						}}
						initialData={initialData}
						handleNotification={handleNotification}
					/>
				) : null}
				<EnrollPlayerModal
					open={openEnrollModal}
					close={() => setOpenEnrollModal(false)}
					playerId={id}
					playerData={player}
					onSuccess={() => getPlayer()}
					handleNotification={handleNotification}
				/>
			</div>
		</Page>
	);
};

export default ScoutingPlayerPage;
